import React, { forwardRef, useState, useEffect, useMemo, useId, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import cs from 'classnames';
import PropTypes from 'prop-types';
import './index.external.css';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose$1(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties$1(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose$1(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);
    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }
  return getRandomValues(rnds8);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];
for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}
function unsafeStringify(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
var native = {
  randomUUID
};

function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }
  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  if (buf) {
    offset = offset || 0;
    for (let i = 0; i < 16; ++i) {
      buf[offset + i] = rnds[i];
    }
    return buf;
  }
  return unsafeStringify(rnds);
}

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : String(i);
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
var _excluded$1 = ["className", "content", "title", "role", "aria-labelledby", "aria-hidden"];

// Custom hook to grab a default iconId using React.useId()
// NOTE: useId is only available in React 18+
var useDefaultIconId = () => {
  var reactId = useId === null || useId === void 0 ? void 0 : useId();
  if (reactId) return "icon_".concat(reactId);
  return undefined;
};
var Icon = /*#__PURE__*/forwardRef((_ref, ref) => {
  var {
      className,
      content,
      title,
      role,
      'aria-labelledby': ariaLabelledby,
      'aria-hidden': ariaHidden
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded$1);
  var [iconId, setIconId] = useState(useDefaultIconId());
  useEffect(() => {
    // If using React < 18, use UUID to render the unique iconId
    // UUID v4 causes hydration issues in SSR because it consumes crypto.
    //    Crypto is a built in node.js package and is not consumed properly in Next.js
    //    So UUID value needs to be set from useEffect which renders client side.
    if (!iconId) {
      setIconId("icon_".concat(v4()));
    }
  }, [iconId]);
  var {
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedby
  } = otherProps;
  var hasLabel = !!title || !!ariaLabelledby || !!ariaLabel || !!ariaDescribedby;
  return useMemo(() => {
    var _content$props;
    return /*#__PURE__*/React.cloneElement(content, _objectSpread2({
      children: /*#__PURE__*/React.createElement(React.Fragment, null, title ? /*#__PURE__*/React.createElement("title", {
        id: iconId
      }, title) : null, content === null || content === void 0 ? void 0 : (_content$props = content.props) === null || _content$props === void 0 ? void 0 : _content$props.children),
      className,
      ref,
      focusable: false,
      role,
      'aria-labelledby': ariaLabelledby || (title ? iconId : undefined),
      'aria-hidden': typeof ariaHidden !== 'undefined' ? ariaHidden : !hasLabel
    }, otherProps));
  }, [content, className, title, role, hasLabel, ref, ariaLabelledby, ariaHidden, otherProps, iconId]);
});
var propTypes$2 = {
  /** Node for the individual icon import */
  content: PropTypes.node.isRequired,
  /** Optional class to be applied to the SVG element */
  className: PropTypes.string,
  /** Value for the <title> element describing the icon */
  title: PropTypes.string,
  role: PropTypes.string,
  /** By default points to the <title> element, if provided. */
  'aria-labelledby': PropTypes.string,
  /** By default is set to 'true' if no title or label is provided. */
  'aria-hidden': PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
};
var defaultProps$2 = {
  className: null,
  title: '',
  role: 'img',
  'aria-labelledby': undefined,
  'aria-hidden': undefined
};
Icon.propTypes = propTypes$2;
Icon.defaultProps = defaultProps$2;

// DO NOT EDIT - THIS FILE IS GENERATED
var closeIcon = /*#__PURE__*/React.createElement("svg", {
  height: "16",
  width: "16",
  viewBox: "0 0 16 16",
  xmlns: "http://www.w3.org/2000/svg"
}, /*#__PURE__*/React.createElement("path", {
  d: "m13.82 1.82-.35-.35L8 6.94 3.24 2.18a1 1 0 0 0-1.42 0l-.35.35L6.94 8l-4.76 4.76a1 1 0 0 0 0 1.42l.35.35L8 9.06l4.76 4.76a1 1 0 0 0 1.42 0l.35-.35L9.06 8l4.76-4.76a1 1 0 0 0 0-1.42Z",
  fill: "currentColor",
  fillRule: "evenodd"
}));

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var candidateSelectors = ['input', 'select', 'textarea', 'a[href]', 'button', '[tabindex]', 'audio[controls]', 'video[controls]', '[contenteditable]:not([contenteditable="false"])'];
var candidateSelector = candidateSelectors.join(',');
var matches = typeof Element === 'undefined' ? function () {} : Element.prototype.matches || Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
function tabbable(el, options) {
  options = options || {};
  var regularTabbables = [];
  var orderedTabbables = [];
  var candidates = el.querySelectorAll(candidateSelector);
  if (options.includeContainer) {
    if (matches.call(el, candidateSelector)) {
      candidates = Array.prototype.slice.apply(candidates);
      candidates.unshift(el);
    }
  }
  var i, candidate, candidateTabindex;
  for (i = 0; i < candidates.length; i++) {
    candidate = candidates[i];
    if (!isNodeMatchingSelectorTabbable(candidate)) continue;
    candidateTabindex = getTabindex(candidate);
    if (candidateTabindex === 0) {
      regularTabbables.push(candidate);
    } else {
      orderedTabbables.push({
        documentOrder: i,
        tabIndex: candidateTabindex,
        node: candidate
      });
    }
  }
  var tabbableNodes = orderedTabbables.sort(sortOrderedTabbables).map(function (a) {
    return a.node;
  }).concat(regularTabbables);
  return tabbableNodes;
}
tabbable.isTabbable = isTabbable;
tabbable.isFocusable = isFocusable;
function isNodeMatchingSelectorTabbable(node) {
  if (!isNodeMatchingSelectorFocusable(node) || isNonTabbableRadio(node) || getTabindex(node) < 0) {
    return false;
  }
  return true;
}
function isTabbable(node) {
  if (!node) throw new Error('No node provided');
  if (matches.call(node, candidateSelector) === false) return false;
  return isNodeMatchingSelectorTabbable(node);
}
function isNodeMatchingSelectorFocusable(node) {
  if (node.disabled || isHiddenInput(node) || isHidden(node)) {
    return false;
  }
  return true;
}
var focusableCandidateSelector = candidateSelectors.concat('iframe').join(',');
function isFocusable(node) {
  if (!node) throw new Error('No node provided');
  if (matches.call(node, focusableCandidateSelector) === false) return false;
  return isNodeMatchingSelectorFocusable(node);
}
function getTabindex(node) {
  var tabindexAttr = parseInt(node.getAttribute('tabindex'), 10);
  if (!isNaN(tabindexAttr)) return tabindexAttr;
  // Browsers do not return `tabIndex` correctly for contentEditable nodes;
  // so if they don't have a tabindex attribute specifically set, assume it's 0.
  if (isContentEditable(node)) return 0;
  return node.tabIndex;
}
function sortOrderedTabbables(a, b) {
  return a.tabIndex === b.tabIndex ? a.documentOrder - b.documentOrder : a.tabIndex - b.tabIndex;
}
function isContentEditable(node) {
  return node.contentEditable === 'true';
}
function isInput(node) {
  return node.tagName === 'INPUT';
}
function isHiddenInput(node) {
  return isInput(node) && node.type === 'hidden';
}
function isRadio(node) {
  return isInput(node) && node.type === 'radio';
}
function isNonTabbableRadio(node) {
  return isRadio(node) && !isTabbableRadio(node);
}
function getCheckedRadio(nodes) {
  for (var i = 0; i < nodes.length; i++) {
    if (nodes[i].checked) {
      return nodes[i];
    }
  }
}
function isTabbableRadio(node) {
  if (!node.name) return true;
  // This won't account for the edge case where you have radio groups with the same
  // in separate forms on the same page.
  var radioSet = node.ownerDocument.querySelectorAll('input[type="radio"][name="' + node.name + '"]');
  var checked = getCheckedRadio(radioSet);
  return !checked || checked === node;
}
function isHidden(node) {
  // offsetParent being null will allow detecting cases where an element is invisible or inside an invisible element,
  // as long as the element does not use position: fixed. For them, their visibility has to be checked directly as well.
  return node.offsetParent === null || getComputedStyle(node).visibility === 'hidden';
}
var tabbable_1 = tabbable;

var tabbable$1 = /*@__PURE__*/getDefaultExportFromCjs(tabbable_1);

var FocusTrap = _ref => {
  var {
    children,
    openFocusRef,
    closeFocusRef,
    childRef
  } = _ref;
  var beforeRef = useRef();
  var afterRef = useRef();

  // Trap focus within container element
  useEffect(() => {
    var handleFocus = ev => {
      var {
        target
      } = ev;
      var tabbables = tabbable$1(childRef.current);
      if (target === beforeRef.current) {
        tabbables[tabbables.length - 1].focus();
      } else if (target === afterRef.current) {
        tabbables[0].focus();
      }
    };
    document.addEventListener('focusin', handleFocus);
    return () => {
      document.removeEventListener('focusin', handleFocus);
    };
  }, [childRef]);

  // Move focus to and from focus trap on mount/unmount
  useEffect(() => {
    var lastFocused = document.activeElement;
    var closeFocus = closeFocusRef ? closeFocusRef.current : null;
    if (openFocusRef) {
      openFocusRef.current.focus();
    } else {
      var tabbables = tabbable$1(childRef.current);
      if (tabbables.length) {
        tabbables[0].focus();
      }
    }
    return () => {
      if (closeFocus) {
        closeFocus.focus();
      } else {
        lastFocused.focus();
      }
    };
  }, [closeFocusRef, openFocusRef, childRef]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    "aria-hidden": true,
    tabIndex: "0",
    ref: beforeRef
  }), children, /*#__PURE__*/React.createElement("div", {
    "aria-hidden": true,
    tabIndex: "0",
    ref: afterRef
  }));
};
var propTypes$1 = {
  children: PropTypes.node,
  openFocusRef: PropTypes.shape({
    current: PropTypes.object
  }),
  closeFocusRef: PropTypes.shape({
    current: PropTypes.object
  }),
  childRef: PropTypes.shape({
    current: PropTypes.object
  }).isRequired
};
var defaultProps$1 = {
  children: undefined,
  openFocusRef: undefined,
  closeFocusRef: undefined
};
FocusTrap.propTypes = propTypes$1;
FocusTrap.defaultProps = defaultProps$1;

var canUseDOM$1 = typeof document !== 'undefined';
var usePortal = () => {
  var portalContainerRef = useRef();
  if (canUseDOM$1 && !portalContainerRef.current) {
    portalContainerRef.current = window.document.createElement('div');
  }

  // Portal DOM node
  useLayoutEffect(() => {
    document.body.appendChild(portalContainerRef.current);
    return () => {
      var el = portalContainerRef.current;
      el.parentNode.removeChild(el);
    };
  }, []);
  return portalContainerRef;
};
var useAriaHideSiblings = (active, selector, ref) => {
  // Manage aria-hidden on other DOM nodes
  useEffect(() => {
    if (active) {
      var rootNodes = document.querySelectorAll(selector);
      var transformedNodes = Array.prototype.map.call(rootNodes, node => {
        // eslint-disable-next-line sonarjs/no-duplicate-string
        var ariaHidden = node.getAttribute('aria-hidden');

        // Side-effect in map!
        if (node !== ref) {
          node.setAttribute('aria-hidden', 'true');
        }
        return {
          node,
          ariaHidden
        };
      });
      if (ref.getAttribute('aria-hidden') !== null) {
        ref.removeAttribute('aria-hidden');
      }
      return () => {
        transformedNodes.forEach(item => {
          if (item.ariaHidden === null) {
            item.node.removeAttribute('aria-hidden');
          } else {
            item.node.setAttribute('aria-hidden', item.ariaHidden);
          }
        });
      };
    }
    return () => {};
  }, [active, ref, selector]);
};
var useHandleEscapeKey = (active, callback) => {
  // Escape key handling
  useEffect(() => {
    var handleEscapeKey = ev => {
      var escapeCode = 27;
      if (ev.keyCode === escapeCode) {
        callback();
      }
    };
    if (active) {
      window.addEventListener('keydown', handleEscapeKey);
    }
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [active, callback]);
};
var modalsOpen = 0;
var usePreventBodyScroll = active => {
  useEffect(() => {
    if (active && canUseDOM$1) {
      Object.assign(document.body.style, {
        top: "-".concat(window.pageYOffset, "px"),
        position: 'fixed',
        width: '100%'
      });
      modalsOpen += 1;
      return () => {
        modalsOpen -= 1;
        if (modalsOpen < 1) {
          var scrollY = document.body.style.top;
          Object.assign(document.body.style, {
            position: '',
            top: '',
            width: ''
          });
          window.scrollTo(0, parseInt(scrollY || 0, 10) * -1);
        }
      };
    }
    return () => {};
  }, [active]);
};

var styles = {"modalContainer":"modalContainer-1vSmX","modal":"modal-1vSmX","fullScreenContainer":"fullScreenContainer-1vSmX","fullScreenModal":"fullScreenModal-1vSmX","fullScreenMobileContainer":"fullScreenMobileContainer-1vSmX","fullScreenMobileModal":"fullScreenMobileModal-1vSmX","backdrop":"backdrop-1vSmX","closeButton":"closeButton-1vSmX"};

var _excluded = ["cancelProps", "children", "classes", "closeFocusRef", "fullScreen", "fullScreenMobile", "messages", "onClose", "onRequestClose", "openFocusRef", "primaryContentSelector", "visible", "voiceOver"];
var canUseDOM = typeof document !== 'undefined';
var Modal = _ref => {
  var {
      cancelProps,
      children,
      classes,
      closeFocusRef,
      fullScreen,
      fullScreenMobile,
      messages,
      onClose,
      onRequestClose,
      openFocusRef,
      primaryContentSelector,
      visible,
      voiceOver
    } = _ref,
    otherProps = _objectWithoutProperties$1(_ref, _excluded);
  var portalContainerRef = usePortal();
  useHandleEscapeKey(visible, onRequestClose);
  usePreventBodyScroll(visible);
  useAriaHideSiblings(visible, primaryContentSelector, portalContainerRef.current);
  var modalRef = useRef();

  // onClose callback
  useEffect(() => {
    return () => {
      if (visible && onClose) {
        onClose();
      }
    };
  }, [visible, onClose]);
  if (!visible || !canUseDOM) {
    return null;
  }
  return (
    /*#__PURE__*/
    // Portal needs to be wrapped in a Fragment for
    // react docgen detection:
    // https://github.com/reactjs/react-docgen/issues/336
    React.createElement(React.Fragment, null, /*#__PURE__*/createPortal( /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: cs(styles.backdrop, classes.backdrop)
    }), /*#__PURE__*/React.createElement("div", {
      "data-testid": "container",
      className: cs(styles.modalContainer, classes.container, {
        [styles.fullScreenContainer]: fullScreen,
        [styles.fullScreenMobileContainer]: fullScreenMobile
      }),
      onClick: ev => {
        // If click is directly on container, meaning it's
        // not on the modal or modal content, you've clicked
        // outside of the modal and can close it
        if (ev.target === ev.currentTarget) {
          onRequestClose(ev);
        }
      }
    }, /*#__PURE__*/React.createElement(FocusTrap, {
      openFocusRef: openFocusRef,
      closeFocusRef: closeFocusRef,
      childRef: modalRef
    }, /*#__PURE__*/React.createElement("div", _extends({
      "aria-label": voiceOver.title,
      "aria-modal": "true",
      role: "dialog",
      className: cs(styles.modal, classes.modal, {
        [styles.fullScreenModal]: fullScreen,
        [styles.fullScreenMobileModal]: fullScreenMobile
      }),
      ref: modalRef
    }, otherProps), /*#__PURE__*/React.createElement("button", _extends({
      type: "button",
      "aria-label": messages.close,
      className: styles.closeButton,
      onClick: onRequestClose
    }, cancelProps), /*#__PURE__*/React.createElement(Icon, {
      content: closeIcon
    })), /*#__PURE__*/React.createElement("div", {
      className: classes.modalContent
    }, children))))), portalContainerRef.current))
  );
};
var propTypes = {
  /** Props set on the cancel button */
  // eslint-disable-next-line react/forbid-prop-types
  cancelProps: PropTypes.object,
  /** Passes modal content */
  children: PropTypes.node,
  /** CSS classNames applied to modal elements */
  classes: PropTypes.shape({
    modal: PropTypes.string,
    backdrop: PropTypes.string,
    container: PropTypes.string,
    modalContent: PropTypes.string
  }),
  /** Use with EXTREME caution.
   * Parameters around use are very narrow, if used incorrectly
   * this causes a11y errors.
   * Element ref for the element that should gain focus when
   * the modal closes. Defaults to the last focused element
   * (the button you clicked to open the modal, typically).
   */
  closeFocusRef: PropTypes.shape({
    current: PropTypes.object
  }),
  /** Flag to enable fullscreen styles on mobile and desktop browsers */
  fullScreen: PropTypes.bool,
  /**  Flag to enable fullscreen styles on mobile browsers */
  fullScreenMobile: PropTypes.bool,
  messages: PropTypes.shape({
    /** Label for the close button */
    close: PropTypes.string.isRequired
  }).isRequired,
  /** Callback fired when the modal closes */
  onClose: PropTypes.func,
  /** Callback fired when a user attempts to close the modal.
   * Use this to update the `visible` prop. */
  onRequestClose: PropTypes.func,
  /** Element ref for the element that should gain focus when
   * the modal opens. Defaults to the first tabbable element
   * in the modal (the close button, usually).
   */
  openFocusRef: PropTypes.shape({
    current: PropTypes.object
  }),
  /** A CSS selector to select your primary page content.
   * This content is hidden from screen readers while the
   * modal is open.
   */
  primaryContentSelector: PropTypes.string,
  /** Flag indicating whether the modal should display or not */
  visible: PropTypes.bool,
  voiceOver: PropTypes.shape({
    /** Label for the modal */
    title: PropTypes.string.isRequired
  })
};
var defaultProps = {
  cancelProps: null,
  children: undefined,
  classes: {},
  closeFocusRef: undefined,
  fullScreenMobile: false,
  fullScreen: false,
  messages: {
    close: 'Close'
  },
  onClose: undefined,
  onRequestClose: undefined,
  openFocusRef: undefined,
  primaryContentSelector: 'body > *',
  visible: false,
  voiceOver: {
    title: 'Modal'
  }
};
Modal.propTypes = propTypes;
Modal.defaultProps = defaultProps;

export { Modal };
//# sourceMappingURL=index.js.map
